import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Auth from '../components/Auth';
import Page from '../components/Page';
import useValidateInvitation from '../queries/useValidateInvitation';
import AlertPopup from '../components/AlertPopup';
import { colors } from '../theme/palette/darkPalette';
import AuthSubmitButton from '../components/AuthSubmitButton';
import { pagesText } from '../constants/pagesText';

const Description = styled(Typography)(({ theme }) => ({
  maxWidth: 375,
  width: '100%',
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 300,
  lineHeight: '16px',
  textAlign: 'center',
  color: theme.palette.text.primary,
}));

const EmailInvitation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isNew, token } = location.state || {};
  const { data: response } = useValidateInvitation({ token });

  const title = pagesText.emailInvitationPage.title[isNew ? 'setupAccount' : 'acceptInvitation'];
  const buttonText =
    pagesText.emailInvitationPage.buttonText[isNew ? 'setupAccount' : 'acceptInvitation'];

  const onNavigate = useCallback(() => {
    // The email is never carried into the activation flow as a parameter; the token identifies
    // the invitation and the server resolves the invited email from it.
    if (isNew) {
      navigate(`/setup-account?token=${encodeURIComponent(token)}`);
      return;
    }

    const params = new URLSearchParams({
      email: response.email,
      customerId: response.customerId,
    });

    navigate(`/login?${params.toString()}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, token, response]);

  const MemoizedActions = useCallback(
    () => (
      <Stack direction="column" alignItems="center" gap={2}>
        <AuthSubmitButton onClick={onNavigate}>{buttonText}</AuthSubmitButton>
      </Stack>
    ),
    [buttonText, onNavigate],
  );

  if (token && !response) return null;

  if (!token || !response.isValid) {
    return (
      <Page title={pagesText.emailValidationPopup.title}>
        <AlertPopup
          title={pagesText.emailValidationPopup.title}
          backgroundColor={colors.darkGrey}
          styles={{
            height: 222,
          }}>
          <Description>{pagesText.emailValidationPopup.description}</Description>
        </AlertPopup>
      </Page>
    );
  }

  return (
    <Page title={title}>
      <Auth title={title} Actions={MemoizedActions} />
    </Page>
  );
};

export default EmailInvitation;
